/**
 * Web automation site detail — attach/detach/reorder agents (SiteAgents),
 * show script availability, and queue Pending runs that the desktop
 * runner polls and executes.
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil as EditIcon, Trash2 as DeleteIcon, Plus as AddIcon, Unlink as DetachIcon, ArrowUp as UpIcon, ArrowDown as DownIcon, Play as RunIcon, Globe as SiteIcon, Code as ScriptIcon } from 'lucide-react';
import {
  webAutomationService,
  type WebAutomationSiteDto,
  type WebAutomationSiteTaskDto,
  type WebAutomationScriptDto,
} from '../services/webAutomationService';
import { agentService } from '../services/agentService';
import type { AgentDefinitionDto, AgentParameterDto } from '../types/agent';
import { ParameterForm } from '../components/sessions/ParameterForm';
import { AGENT_DROPDOWN, agentToDropdownOption } from '../components/agents/agentDropdown';
import { useNotificationStore } from '../store/notificationStore';
import { useContextStore } from '../store/contextStore';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import EmptyState from '../components/data-display/EmptyState';
import { Button, Chip, CoordioDropdown, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, useStaticDropdownSource } from '@coordio/ui';
import { FormSection, InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { useConfirmDialog } from '../components/common/ConfirmDialog';
import { WebAutomationDslEditor } from '../components/webAutomation/WebAutomationDslEditor';

export const WebAutomationSiteDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const hasContext = Boolean(currentTenant && currentProject);

  const [site, setSite] = useState<WebAutomationSiteDto | null>(null);
  const [siteTasks, setSiteTasks] = useState<WebAutomationSiteTaskDto[]>([]);
  const [allAgents, setAllAgents] = useState<AgentDefinitionDto[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);
  const [scriptDialogTask, setScriptDialogTask] = useState<WebAutomationSiteTaskDto | null>(null);
  const [scriptDetail, setScriptDetail] = useState<WebAutomationScriptDto | null>(null);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptRemoving, setScriptRemoving] = useState(false);
  const [scriptEditing, setScriptEditing] = useState(false);
  const [scriptDraft, setScriptDraft] = useState('');
  const [scriptSaving, setScriptSaving] = useState(false);
  const [runInputTask, setRunInputTask] = useState<WebAutomationSiteTaskDto | null>(null);
  const [runInputParameters, setRunInputParameters] = useState<AgentParameterDto[]>([]);
  const [runInputValues, setRunInputValues] = useState<Record<string, any>>({});
  const [runInputError, setRunInputError] = useState<string | null>(null);
  const [queueing, setQueueing] = useState(false);

  useEffect(() => {
    if (id && hasContext) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentTenant?.id, currentProject?.id]);

  const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [siteData, siteTaskData, agentsResult] = await Promise.all([
        webAutomationService.getSite(id),
        webAutomationService.getSiteTasks(id),
        agentService.getAgents({ pageSize: 200, isActive: true }),
      ]);
      setSite(siteData);
      setSiteTasks(siteTaskData);
      setAllAgents(agentsResult.items ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to load site');
      addNotification('Failed to load site', 'error');
    } finally {
      setLoading(false);
    }
  };

  const reloadSiteTasks = async () => {
    if (!id) return;
    try {
      setSiteTasks(await webAutomationService.getSiteTasks(id));
    } catch {
      // list refresh failure is non-fatal; keep current data
    }
  };

  const handleAttach = async () => {
    if (!id || !selectedAgentId) return;
    setAttaching(true);
    try {
      await webAutomationService.attachAgent(id, selectedAgentId);
      addNotification('Agent attached — it can now be executed on the desktop runner', 'success');
      setSelectedAgentId('');
      reloadSiteTasks();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to attach agent', 'error');
    } finally {
      setAttaching(false);
    }
  };

  const handleDetach = async (siteTaskId: string) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Detach task',
      message: 'Detach this task? Its scripts and run history for this site will be deleted.',
      confirmText: 'Detach',
    });
    if (!ok) return;
    try {
      await webAutomationService.detachAgent(id, siteTaskId);
      addNotification('Task detached', 'success');
      reloadSiteTasks();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to detach task', 'error');
    }
  };

  const handleMove = async (siteTaskId: string, direction: -1 | 1) => {
    if (!id) return;
    const index = siteTasks.findIndex((st) => st.id === siteTaskId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= siteTasks.length) return;

    const reordered = [...siteTasks];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setSiteTasks(reordered);
    try {
      await webAutomationService.reorderSiteTasks(id, reordered.map((st) => st.id));
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to reorder tasks', 'error');
      reloadSiteTasks();
    }
  };

  const handleViewScript = async (siteTask: WebAutomationSiteTaskDto) => {
    setScriptDialogTask(siteTask);
    setScriptDetail(null);
    setScriptEditing(false);
    setScriptLoading(true);
    try {
      setScriptDetail(await webAutomationService.getCachedScript(siteTask.id));
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to load script', 'error');
      setScriptDialogTask(null);
    } finally {
      setScriptLoading(false);
    }
  };

  const handleStartEditScript = () => {
    if (!scriptDetail) return;
    setScriptDraft(formatDslJson(scriptDetail.dslJson));
    setScriptEditing(true);
  };

  const handleSaveScript = async () => {
    if (!scriptDialogTask) return;
    try {
      JSON.parse(scriptDraft);
    } catch {
      addNotification('Script is not valid JSON', 'error');
      return;
    }
    const ok = await confirm({
      title: 'Save script version',
      message: 'Save the edited script as a new version? The desktop runner will use it on the next run.',
      confirmText: 'Save',
      confirmColor: 'primary',
    });
    if (!ok) return;
    setScriptSaving(true);
    try {
      const saved = await webAutomationService.saveScript(
        scriptDialogTask.id,
        scriptDraft,
        'manual',
        'Edited in Web UI',
      );
      setScriptDetail(saved);
      setScriptEditing(false);
      addNotification(`Script saved as v${saved.version}`, 'success');
      reloadSiteTasks();
    } catch (err: unknown) {
      const data = (err as {
        response?: {
          data?: { message?: string; title?: string; errors?: string[] | Record<string, string[]> };
        };
      })?.response?.data;
      let details = '';
      if (Array.isArray(data?.errors)) {
        details = ` ${data.errors.join(' ')}`;
      } else if (data?.errors && typeof data.errors === 'object') {
        details = ` ${Object.values(data.errors).flat().join(' ')}`;
      }
      addNotification((data?.message || data?.title || 'Failed to save script') + details, 'error');
    } finally {
      setScriptSaving(false);
    }
  };

  const handleRemoveScript = async () => {
    if (!scriptDialogTask) return;
    const ok = await confirm({
      title: 'Remove saved script',
      message: 'Remove the current saved script? The next run will regenerate it.',
      confirmText: 'Remove',
    });
    if (!ok) return;
    setScriptRemoving(true);
    try {
      await webAutomationService.invalidateScriptCache(scriptDialogTask.id);
      addNotification('Saved script removed', 'success');
      setScriptDialogTask(null);
      reloadSiteTasks();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to remove script';
      addNotification(msg, 'error');
    } finally {
      setScriptRemoving(false);
    }
  };

  const formatDslJson = (dslJson: string) => {
    try {
      return JSON.stringify(JSON.parse(dslJson), null, 2);
    } catch {
      return dslJson;
    }
  };

  const queueRun = async (siteTaskId: string, inputValues?: Record<string, any>) => {
    const hasInputs = inputValues && Object.keys(inputValues).length > 0;
    await webAutomationService.createRun({
      siteTaskId,
      status: 'Pending',
      inputParametersJson: hasInputs ? JSON.stringify(inputValues) : undefined,
    });
    addNotification('Run queued — the desktop runner will pick it up', 'success');
  };

  const handleQueueRun = async (siteTask: WebAutomationSiteTaskDto) => {
    try {
      const agent = await agentService.getAgent(siteTask.agentDefinitionId);
      const parameters = agent.parameters ?? [];
      if (parameters.length === 0) {
        await queueRun(siteTask.id);
        return;
      }

      const defaults: Record<string, any> = {};
      parameters.forEach((p) => {
        if (p.defaultValue !== undefined && p.defaultValue !== null && p.defaultValue !== '') {
          defaults[p.key] = p.defaultValue;
        }
      });
      setRunInputParameters(parameters);
      setRunInputValues(defaults);
      setRunInputError(null);
      setRunInputTask(siteTask);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to queue run', 'error');
    }
  };

  const handleQueueRunWithInputs = async () => {
    if (!runInputTask) return;

    const missing = runInputParameters
      .filter((p) => {
        const value = runInputValues[p.key];
        return p.isRequired && (value === undefined || value === null || value === '');
      })
      .map((p) => p.label || p.key);
    if (missing.length > 0) {
      setRunInputError(`Missing required parameters: ${missing.join(', ')}`);
      return;
    }

    setQueueing(true);
    setRunInputError(null);
    try {
      await queueRun(runInputTask.id, runInputValues);
      setRunInputTask(null);
    } catch (err: any) {
      setRunInputError(err.response?.data?.message || 'Failed to queue run');
    } finally {
      setQueueing(false);
    }
  };

  const attachedAgentIds = useMemo(
    () => new Set(siteTasks.map((st) => st.agentDefinitionId).filter((x): x is string => Boolean(x))),
    [siteTasks],
  );
  const attachableOptions = useMemo(() => {
    const attachable = allAgents.filter((a) => !attachedAgentIds.has(a.id));
    if (attachable.length === 0) {
      return [{ value: '', label: 'No unattached agents — create one in the Agent Registry', disabled: true }];
    }
    return attachable.map(agentToDropdownOption);
  }, [allAgents, attachedAgentIds]);
  const attachableSource = useStaticDropdownSource(attachableOptions);

  const columns: DataTableColumn<WebAutomationSiteTaskDto>[] = [
    {
      id: 'order',
      label: 'Order',
      minWidth: 90,
      render: (st) => {
        const index = siteTasks.findIndex((x) => x.id === st.id);
        return (
          <div className="flex items-center gap-1">
            <span className="text-sm">{index + 1}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" disabled={index === 0} onClick={() => handleMove(st.id, -1)}>
                    <UpIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Move up</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={index === siteTasks.length - 1}
                    onClick={() => handleMove(st.id, 1)}
                  >
                    <DownIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Move down</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    {
      id: 'taskName',
      label: 'Agent',
      minWidth: 220,
      render: (st) => (
        <div>
          <p className="text-sm font-medium">
            {st.taskName}
            {st.agentSlug ? (
              <span className="text-xs text-[var(--color-text-secondary)] ml-1">({st.agentSlug})</span>
            ) : null}
          </p>
          <span
            className="text-xs text-[var(--color-text-secondary)]"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {st.promptDefinition}
          </span>
        </div>
      ),
    },
    {
      id: 'script',
      label: 'Script',
      minWidth: 120,
      render: (st) => (
        <Chip
          label={st.hasScript ? `Yes (v${st.currentScriptVersion})` : 'No'}
          color={st.hasScript ? 'green' : 'default'}
          variant={st.hasScript ? undefined : 'outlined'}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 200,
      align: 'right',
      render: (st) => (
        <div className="flex gap-1 justify-end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="text-[var(--color-accent-primary)]" onClick={() => handleQueueRun(st)}>
                  <RunIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Queue run for desktop runner</TooltipContent>
            </Tooltip>
            {st.hasScript && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" onClick={() => handleViewScript(st)}>
                    <ScriptIcon size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View saved script</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={() => navigate(`/web-automation/runs?siteTaskId=${st.id}`)}>
                  <SiteIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View runs</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleDetach(st.id)}>
                  <DetachIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Detach agent</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  const handleDeleteSite = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Delete site',
      message: 'Delete this site? All attached site tasks, scripts, and runs will also be deleted.',
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await webAutomationService.deleteSite(id);
      addNotification('Site deleted successfully', 'success');
      navigate('/web-automation/sites');
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete site', 'error');
    }
  };

  if (!hasContext) {
    return (
      <PageDetailLayout
        data-testid="web-automation-site-form-page"
        title="Web Automation Site"
        breadcrumbs={[{ label: 'Web Automation Sites', href: '/web-automation/sites' }]}
        backTo={{ label: 'Sites', href: '/web-automation/sites' }}
      >
        <InlineErrorPanel
          data-testid="web-automation-site-error-panel"
          title="Context required"
          message="Please select a tenant and project first (using the switchers above) to manage this site."
        />
      </PageDetailLayout>
    );
  }

  return (
    <PageDetailLayout
      data-testid="web-automation-site-form-page"
      title={site?.name || 'Site'}
      subtitle={site?.description}
      breadcrumbs={[
        { label: 'Web Automation Sites', href: '/web-automation/sites' },
        { label: site?.name || 'Site' },
      ]}
      backTo={{ label: 'Sites', href: '/web-automation/sites' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      status={
        site
          ? { label: site.isActive ? 'Active' : 'Inactive', color: site.isActive ? 'success' : 'default' }
          : undefined
      }
      chips={
        site
          ? [
              { label: site.url, variant: 'outlined' as const },
              { label: `Auth: ${site.authType}`, variant: 'outlined' as const },
              ...(site.hasCredentials ? [{ label: 'Credentials stored', color: 'info' as const }] : []),
            ]
          : []
      }
      actions={
        site
          ? [
              {
                icon: <EditIcon />,
                label: 'Edit',
                onClick: () => navigate(`/web-automation/sites/${id}/edit`),
                variant: 'outlined' as const,
              },
              {
                icon: <DeleteIcon />,
                label: 'Delete',
                onClick: handleDeleteSite,
                color: 'error' as const,
                variant: 'outlined' as const,
              },
            ]
          : []
      }
    >
      <FormSection data-testid="web-automation-site-section-attachAgent" title="Attach an Agent" description="Select an agent from the registry to create an executable SiteAgent for the desktop runner.">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="min-w-[300px]">
            <CoordioDropdown
              data-testid="web-automation-site-agentSelector"
              source={attachableSource}
              value={selectedAgentId || null}
              onChange={(val) => setSelectedAgentId(val ?? '')}
              {...AGENT_DROPDOWN}
              placeholder="Attach an agent"
              data-pw="attachAgentId"
              data-field-type="select"
            />
          </div>
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            disabled={!selectedAgentId || attaching}
            onClick={handleAttach}
            data-testid="web-automation-site-attach-btn"
          >
            <AddIcon size={16} />
            Attach
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/agents/new')}
            data-testid="web-automation-site-create-agent-btn"
          >
            Create new agent
          </Button>
        </div>
      </FormSection>

      <FormSection data-testid="web-automation-site-section-siteTasks" title="Site Tasks">
        {siteTasks.length === 0 ? (
          <EmptyState
            title="No agents attached"
            description="Attach a registry agent to create an executable SiteAgent for the desktop runner."
          />
        ) : (
          <DataTable data-testid="web-automation-site-tasks-table" columns={columns} rows={siteTasks} />
        )}
      </FormSection>

      {/* Saved script viewer dialog */}
      {scriptDialogTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="web-automation-site-script-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={() => setScriptDialogTask(null)} />
          <div className="relative z-10 w-full max-w-3xl rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-xl max-h-[90vh] flex flex-col">
            {/* Title */}
            <div className="border-b border-[var(--color-border-default)] px-6 py-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold">Saved script — {scriptDialogTask.taskName}</h2>
                {scriptDetail && (
                  <Chip label={`v${scriptDetail.version}`} color="green" />
                )}
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 border-b border-[var(--color-border-default)]">
              {scriptLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSkeleton rows={3} />
                </div>
              ) : !scriptDetail ? (
                <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-3 text-sm">
                  No cached script found for this task. It will be generated by the desktop runner
                  on the next successful run.
                </div>
              ) : (
                <>
                  <div className="flex gap-2 flex-wrap mb-6">
                    <Chip label={`Generated by: ${scriptDetail.generatedBy || 'unknown'}`} variant="outlined" />
                    <Chip label={`Saved: ${new Date(scriptDetail.createdAt).toLocaleString()}`} variant="outlined" />
                    {scriptDetail.notes && (
                      <Chip label={scriptDetail.notes} variant="outlined" />
                    )}
                  </div>

                  <h3 className="text-sm font-semibold mb-2">Script steps (Rich DSL)</h3>
                  {scriptEditing ? (
                    <WebAutomationDslEditor value={scriptDraft} onChange={setScriptDraft} />
                  ) : (
                    <WebAutomationDslEditor value={formatDslJson(scriptDetail.dslJson)} readOnly />
                  )}
                </>
              )}
            </div>
            {/* Actions */}
            <div className="flex justify-between gap-2 px-6 py-4">
              <div>
                {scriptDetail && !scriptEditing && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    data-testid="web-automation-site-remove-script-btn"
                    onClick={handleRemoveScript}
                    disabled={scriptRemoving}
                  >
                    {scriptRemoving ? 'Removing…' : 'Remove script'}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {scriptDetail && !scriptEditing && (
                  <Button type="button" variant="outline" size="sm" className="gap-2" data-testid="web-automation-site-edit-script-btn" onClick={handleStartEditScript}>
                    <EditIcon size={16} />
                    Edit script
                  </Button>
                )}
                {scriptEditing && (
                  <>
                    <Button type="button" variant="ghost" size="sm" data-testid="web-automation-site-discard-script-btn" onClick={() => setScriptEditing(false)} disabled={scriptSaving}>
                      Discard changes
                    </Button>
                    <Button type="button" variant="default" size="sm" data-testid="web-automation-site-save-script-btn" onClick={handleSaveScript} disabled={scriptSaving}>
                      {scriptSaving ? 'Saving…' : 'Save as new version'}
                    </Button>
                  </>
                )}
                {!scriptEditing && (
                  <Button type="button" variant="outline" size="sm" data-testid="web-automation-site-close-script-btn" onClick={() => setScriptDialogTask(null)}>Close</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Run input parameters dialog */}
      {runInputTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="web-automation-site-run-input-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={() => !queueing && setRunInputTask(null)} />
          <div className="relative z-10 w-full max-w-lg rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-xl max-h-[90vh] flex flex-col">
            {/* Title */}
            <div className="border-b border-[var(--color-border-default)] px-6 py-4">
              <h2 className="text-lg font-semibold">Run inputs — {runInputTask.taskName}</h2>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 border-b border-[var(--color-border-default)]">
              <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                This agent needs the following inputs before the run starts. The values are
                passed to the desktop runner as script parameters and task context.
              </p>
              {runInputError && (
                <div className="mb-4">
                  <InlineErrorPanel message={runInputError} action={<Button type="button" variant="ghost" size="sm" onClick={() => setRunInputError(null)}>Dismiss</Button>} />
                </div>
              )}
              <ParameterForm
                parameters={runInputParameters}
                values={runInputValues}
                onChange={setRunInputValues}
              />
            </div>
            {/* Actions */}
            <div className="flex justify-end gap-2 px-6 py-4">
              <Button type="button" variant="ghost" size="sm" data-testid="cancel-web-automation-site-run-btn" onClick={() => setRunInputTask(null)} disabled={queueing}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className="gap-2"
                data-testid="web-automation-site-queue-run-btn"
                onClick={handleQueueRunWithInputs}
                disabled={queueing}
              >
                <RunIcon size={16} />
                {queueing ? 'Queueing…' : 'Queue run'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageDetailLayout>
  );
};
